import React, { useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { getActivityIcon, linkifyActivityUsername } from '../../utils/activityUtil';
import { DEFAULT_USER_AVATAR } from '../../constants/common';
import { PUSH_CONFIG } from './pushConstants';
import { normalizeForegroundPushDetail } from './pushForegroundNormalize';
import { navigateToPushLink } from './pushNavigation';
import {
  canNavigatePushUsername,
  navigatePushUsernameClick,
} from './pushUsernameNavigation';
import {
  playNotificationBannerSound,
  shouldPlayNotificationBannerSound,
} from '../../services/notificationBannerSoundService';

const isLocalDevHost = (hostname) =>
  hostname === 'localhost' ||
  hostname.endsWith('.localhost') ||
  hostname === '127.0.0.1' ||
  hostname === '[::1]' ||
  /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname) ||
  !hostname.includes('.');

const getForegroundPushToastAutoClose = () => {
  if (typeof window === 'undefined') return PUSH_CONFIG.AUTO_CLOSE_MS;
  if (isLocalDevHost(window.location.hostname)) return false;
  return PUSH_CONFIG.AUTO_CLOSE_MS;
};

const FOREGROUND_TOAST_TAG_TTL_MS = 60_000;
const MAX_SEEN_FOREGROUND_TAGS = 1000;
const SWIPE_UP_DISMISS_THRESHOLD_PX = 48;
const SWIPE_UP_AXIS_RATIO = 1.2;
/** Allow react-toastify to unmount before SPA route changes. */
const PUSH_TOAST_NAV_DEFER_MS = 50;

/** @type {Map<string, number>} tag -> timeout id */
const seenForegroundToastTags = new Map();

const clearAllForegroundToastTagTimers = () => {
  if (typeof window === 'undefined') return;
  for (const timeoutId of seenForegroundToastTags.values()) {
    window.clearTimeout(timeoutId);
  }
  seenForegroundToastTags.clear();
};

const renderTextWithMentions = (value, className, username, onUsernameClick) => {
  const text = typeof value === 'string' ? value : '';
  if (!text) return null;

  const content = username
    ? linkifyActivityUsername(text, username, onUsernameClick)
    : text;

  return <p className={className}>{content}</p>;
};

const getToastElement = (element) => element?.closest?.('.Toastify__toast');

const resetToastSwipeStyles = (toastElement) => {
  if (!toastElement) return;
  toastElement.style.transition = '';
  toastElement.style.transform = '';
  toastElement.style.opacity = '';
};

const PushForegroundToastContent = ({
  title,
  body,
  link,
  username,
  activityType,
  avatar,
  useAvatar,
  tag,
}) => {
  const bannerRef = useRef(null);
  const swipeRef = useRef(null);
  const suppressClickRef = useRef(false);

  const navigateFromToast = (navigate) => {
    toast.dismiss(tag);
    window.setTimeout(navigate, PUSH_TOAST_NAV_DEFER_MS);
  };

  const openNotification = () => {
    navigateFromToast(() => navigateToPushLink(link));
  };

  const handleUsernameClick = username
    ? (clickEvent) => {
      clickEvent.stopPropagation();
      navigateFromToast(() => navigatePushUsernameClick(username));
    }
    : undefined;
  const onUsernameClick =
    username && canNavigatePushUsername() ? handleUsernameClick : undefined;

  const clearSwipe = useCallback(() => {
    const swipe = swipeRef.current;
    swipeRef.current = null;
    resetToastSwipeStyles(swipe?.toastElement);
  }, []);

  useEffect(() => {
    const element = bannerRef.current;
    if (!element) return undefined;

    const onTouchStart = (touchEvent) => {
      const touch = touchEvent.touches?.[0];
      if (!touch) return;

      suppressClickRef.current = false;
      swipeRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        toastElement: getToastElement(element),
        isDragging: false,
      };
    };

    const onTouchMove = (touchEvent) => {
      const swipe = swipeRef.current;
      const touch = touchEvent.touches?.[0];
      if (!swipe?.toastElement || !touch) return;

      const deltaX = touch.clientX - swipe.startX;
      const deltaY = touch.clientY - swipe.startY;
      const upwardDistance = -deltaY;
      const isVerticalUpSwipe =
        upwardDistance > 8 &&
        upwardDistance > Math.abs(deltaX) * SWIPE_UP_AXIS_RATIO;

      if (!isVerticalUpSwipe) return;

      swipe.isDragging = true;
      touchEvent.preventDefault();

      const clampedDistance = Math.max(0, upwardDistance);
      swipe.toastElement.style.transition = 'none';
      swipe.toastElement.style.transform = `translateY(-${clampedDistance}px)`;
      swipe.toastElement.style.opacity = `${Math.max(0.35, 1 - clampedDistance / 140)}`;
    };

    const onTouchEnd = (touchEvent) => {
      const swipe = swipeRef.current;
      const touch = touchEvent.changedTouches?.[0];
      if (!swipe || !touch) {
        clearSwipe();
        return;
      }

      const deltaX = touch.clientX - swipe.startX;
      const deltaY = touch.clientY - swipe.startY;
      const upwardDistance = -deltaY;
      const isIntentionalUpSwipe =
        upwardDistance >= SWIPE_UP_DISMISS_THRESHOLD_PX &&
        upwardDistance > Math.abs(deltaX) * SWIPE_UP_AXIS_RATIO;

      if (isIntentionalUpSwipe) {
        suppressClickRef.current = true;
        resetToastSwipeStyles(swipe.toastElement);
        swipeRef.current = null;
        toast.dismiss(tag);
        return;
      }

      if (swipe.isDragging && swipe.toastElement) {
        swipe.toastElement.style.transition = 'transform 180ms ease, opacity 180ms ease';
        resetToastSwipeStyles(swipe.toastElement);
      }

      swipeRef.current = null;
    };

    element.addEventListener('touchstart', onTouchStart, { passive: true });
    element.addEventListener('touchmove', onTouchMove, { passive: false });
    element.addEventListener('touchend', onTouchEnd, { passive: true });
    element.addEventListener('touchcancel', clearSwipe, { passive: true });

    return () => {
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchmove', onTouchMove);
      element.removeEventListener('touchend', onTouchEnd);
      element.removeEventListener('touchcancel', clearSwipe);
      clearSwipe();
    };
  }, [tag, clearSwipe]);

  const handleClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }

    openNotification();
  };

  return (
    <div
      ref={bannerRef}
      className="push-header-toast"
      role="button"
      tabIndex={0}
      aria-label="Open notification"
      onClick={handleClick}
      onKeyDown={(keyEvent) => {
        if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
          keyEvent.preventDefault();
          openNotification();
        }
      }}
    >
      {useAvatar ? (
        <img
          src={avatar || DEFAULT_USER_AVATAR}
          alt="Notification"
          className="push-header-toast__icon"
          onError={(imageEvent) => {
            imageEvent.target.onerror = null;
            imageEvent.target.src = DEFAULT_USER_AVATAR;
          }}
        />
      ) : (
        <span className="push-header-toast__type-icon">{getActivityIcon(activityType)}</span>
      )}
      <div className="push-header-toast__content">
        {renderTextWithMentions(
          title,
          'push-header-toast__title',
          username,
          onUsernameClick
        )}
        {renderTextWithMentions(
          body,
          'push-header-toast__body',
          username,
          onUsernameClick
        )}
      </div>
    </div>
  );
};

export const showForegroundPushToast = (rawDetail) => {
  const detail = normalizeForegroundPushDetail(rawDetail || {});
  const { title, body, link, username, activityType, avatar, useAvatar, tag } = detail;

  if (tag && seenForegroundToastTags.has(tag)) {
    return;
  }
  if (tag && typeof window !== 'undefined') {
    if (seenForegroundToastTags.size >= MAX_SEEN_FOREGROUND_TAGS) {
      clearAllForegroundToastTagTimers();
    }
    const timeoutId = window.setTimeout(() => {
      seenForegroundToastTags.delete(tag);
    }, FOREGROUND_TOAST_TAG_TTL_MS);
    seenForegroundToastTags.set(tag, timeoutId);
  }

  if (shouldPlayNotificationBannerSound(rawDetail, detail)) {
    void playNotificationBannerSound();
  }

  toast(
    <PushForegroundToastContent
      title={title}
      body={body}
      link={link}
      username={username}
      activityType={activityType}
      avatar={avatar}
      useAvatar={useAvatar}
      tag={tag}
    />,
    {
      toastId: tag,
      containerId: 'push-header-notifications',
      position: 'top-center',
      autoClose: getForegroundPushToastAutoClose(),
      hideProgressBar: true,
      closeButton: false,
      closeOnClick: false,
      icon: false,
      draggable: false,
      className: 'push-header-toast-wrapper',
      bodyClassName: 'push-header-toast-container',
    }
  );
};
